import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, parseErrorDetail } from "../lib/api";

const POLICY_HINT =
  "At least 12 characters and must include an upper-case letter, a lower-case letter, a digit, and a symbol.";
const meetsPolicy = (pw) =>
  typeof pw === "string" &&
  pw.length >= 12 &&
  /[A-Z]/.test(pw) &&
  /[a-z]/.test(pw) &&
  /[0-9]/.test(pw) &&
  /[^A-Za-z0-9\s]/.test(pw);

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!meetsPolicy(newPw)) {
      setError("New password does not meet the policy. " + POLICY_HINT);
      return;
    }
    if (newPw !== confirmPw) {
      setError("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", {
        token,
        new_password: newPw,
        confirm_password: confirmPw,
      });
      nav("/login", { replace: true, state: { pwReset: true } });
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apwo-auth-shell">
      <div className="apwo-auth-container">
        <div className="apwo-auth-card" role="dialog" aria-labelledby="apwo-rpw-title" data-testid="reset-card">
          <header className="apwo-auth-header">
            <p className="apwo-brand-eyebrow">AUTOPARTSB2B</p>
            <h1 id="apwo-rpw-title" className="apwo-auth-title" data-testid="reset-title">Choose a new password</h1>
            <p className="apwo-auth-sub">
              This link is single-use and expires 20 minutes after it was sent.
              After you save, you&rsquo;ll be signed out from any existing devices
              and must sign in again with the new password.
            </p>
          </header>

          <form className="apwo-auth-form" onSubmit={onSubmit} noValidate autoComplete="off" data-testid="reset-form">
            <label className="apwo-field">
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={12}
                data-testid="reset-new"
              />
            </label>
            <label className="apwo-field">
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                minLength={12}
                data-testid="reset-confirm"
              />
            </label>
            <p className="apwo-fineprint" data-testid="reset-policy">{POLICY_HINT}</p>
            {error && (
              <div className="apwo-alert-error" role="alert" data-testid="reset-error">{error}</div>
            )}
            <button
              type="submit"
              className="apwo-btn-primary"
              disabled={submitting || !newPw || !confirmPw}
              data-testid="reset-submit"
            >
              {submitting ? "Saving…" : "Save new password"}
            </button>
          </form>

          <footer className="apwo-auth-footer">
            <p className="apwo-support-line">
              <Link to="/login" data-testid="reset-back-to-login">Back to sign in</Link>
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
